import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronRight,
  Download,
  List,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  UserPlus,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  employeeService,
  payrollOpsService,
  salaryStructureService,
} from "../../../services/api";
import { displayLabel, employeeDisplayName } from "../../../utils/displayLabel";
import { formatInr } from "./runPayrollUtils";
import { PAYROLL_ACTION_ICONS } from "./payrollActionChrome";
import { ActionInfoTip, ActionProcessCard } from "../shared/ModuleActionsFrame";
import { exportAssignedStructureReportExcel } from "./exportAssignedStructureReportExcel";

function defaultYm(payMonth) {
  const s = String(payMonth || "").slice(0, 7);
  if (/^\d{4}-\d{2}$/.test(s)) return s;
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s === "paid" || s === "approved" || s === "active")
    return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (s === "ready" || s === "submitted")
    return "bg-sky-50 text-sky-800 border-sky-200";
  if (s === "hold" || s === "clarification" || s === "partial")
    return "bg-amber-50 text-amber-900 border-amber-200";
  if (s === "rejected" || s === "cancelled" || s === "reversed")
    return "bg-red-50 text-red-800 border-red-200";
  if (s === "draft") return "bg-slate-100 text-slate-700 border-slate-200";
  return "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))]";
}

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

/** Extra columns per action (beyond Sl No / Emp Code / Name / Designation / Department / Status). */
const EXTRA_COLUMNS = {
  assigned_structure: [
    { key: "structureName", label: "Structure" },
    { key: "effectiveFrom", label: "Effective", render: (r) => formatDate(r.effectiveFrom) },
    {
      key: "annualCtc",
      label: "Annual CTC",
      align: "right",
      render: (r) => formatInr(r.annualCtc),
    },
  ],
  salary_revision: [
    { key: "effectiveDate", label: "Effective", render: (r) => formatDate(r.effectiveDate) },
    {
      key: "annualCtc",
      label: "New CTC",
      align: "right",
      render: (r) => formatInr(r.annualCtc),
    },
    { key: "reason", label: "Reason" },
  ],
  update_statutory: [
    { key: "effectiveFrom", label: "Effective", render: (r) => formatDate(r.effectiveFrom) },
    { key: "pf", label: "PF" },
    { key: "esi", label: "ESI" },
    { key: "pt", label: "PT" },
  ],
  tax_planning: [
    { key: "financialYear", label: "FY" },
    { key: "taxRegime", label: "Regime" },
  ],
  loan: [
    {
      key: "amount",
      label: "Amount",
      align: "right",
      render: (r) => formatInr(r.amount),
    },
    { key: "tenureMonths", label: "Tenure" },
    { key: "purpose", label: "Purpose" },
  ],
  leave_encashment: [
    { key: "days", label: "Days" },
    { key: "payMonth", label: "Pay month", render: (r) => displayLabel(r.payMonth) },
  ],
  advance: [],
  reimbursement: [
    { key: "headName", label: "Component" },
    {
      key: "claimAmount",
      label: "Amount",
      align: "right",
      render: (r) => formatInr(r.claimAmount),
    },
    { key: "financialYear", label: "FY" },
  ],
};

const ACTION_META = {
  assigned_structure: {
    processTitle: "Select employee",
    listTitle: "Assigned list",
    processHint:
      "List shows only employees with an active assigned structure. Search here to open any employee’s structure.",
    emptyList: "No assigned salary structures yet.",
    openLabel: "Structure",
  },
  salary_revision: {
    processTitle: "Select employee",
    listTitle: "Revision list",
    processHint:
      "List shows existing revision records only. Select any employee here to create or review a revision.",
    emptyList: "No salary revisions recorded yet.",
    openLabel: "Revise",
  },
  update_statutory: {
    processTitle: "Select employee",
    listTitle: "Updated list",
    processHint:
      "List shows statutory applicability history. Select an employee here to update PF / ESI / PT and related flags.",
    emptyList: "No statutory updates recorded yet.",
    openLabel: "Update",
  },
  tax_planning: {
    processTitle: "Select employee",
    listTitle: "Declaration list",
    processHint:
      "List shows declarations already filed for the financial year. Select an employee here to open tax planning.",
    emptyList: "No tax declarations for this year yet.",
    openLabel: "Open",
  },
  loan: {
    processTitle: "Select employee",
    listTitle: "Loan list",
    processHint:
      "List shows existing loan applications only. Select an employee here to apply or manage a loan.",
    emptyList: "No loan applications yet.",
    openLabel: "Loan",
  },
  leave_encashment: {
    processTitle: "Select employee",
    listTitle: "Encashment list",
    processHint:
      "List shows existing encashment requests only. Select an employee here to request leave encashment.",
    emptyList: "No leave encashment requests yet.",
    openLabel: "Encash",
  },
  advance: {
    processTitle: "Select employee",
    listTitle: "Advance list",
    processHint:
      "Advance register appears in the list once advances are recorded. Select an employee here to process an advance.",
    emptyList: "No salary advances recorded yet.",
    openLabel: "Advance",
  },
  reimbursement: {
    processTitle: "Select employee",
    listTitle: "Claims list",
    processHint:
      "List shows existing flexi / reimbursement claims only. Select an employee here to open claims.",
    emptyList: "No reimbursement claims yet.",
    openLabel: "Claims",
  },
};

/**
 * Shared Payroll Actions body — Process (pick one) + List (actioned records only).
 * Same framework as Process F&F; options/functions differ per action.
 */
export default function PayrollActionEmployeePanel({
  darkMode = false,
  actionKey,
  title,
  blurb,
  payMonth = "",
  financialYear = "",
  onOpenEmployee,
  openLabel,
}) {
  const meta = ACTION_META[actionKey] || {
    processTitle: "Process",
    listTitle: "List",
    processHint: blurb || "",
    emptyList: "No records yet.",
    openLabel: openLabel || "Open",
  };
  const Icon3d = PAYROLL_ACTION_ICONS[actionKey];
  const extraCols = EXTRA_COLUMNS[actionKey] || [];
  const PAGE_SIZE = 50;

  const [panel, setPanel] = useState("process");
  const [employeeId, setEmployeeId] = useState("");
  const [picked, setPicked] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listQ, setListQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const isAssignedStructure = actionKey === "assigned_structure";
  const [reportMonth, setReportMonth] = useState(() => defaultYm(payMonth));
  const [structureFilter, setStructureFilter] = useState("All");
  const [structureOptions, setStructureOptions] = useState(["All"]);
  const [exporting, setExporting] = useState(false);

  const border = "border-[hsl(var(--ds-primary)/0.35)]";
  const muted = "text-[hsl(var(--ds-muted-fg))]";

  useEffect(() => {
    setReportMonth(defaultYm(payMonth));
  }, [payMonth]);

  useEffect(() => {
    if (!isAssignedStructure) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await salaryStructureService.listTemplates();
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];
        const names = [
          ...new Set(
            list
              .map((t) => String(t?.name || "").trim())
              .filter(Boolean)
          ),
        ].sort((a, b) => a.localeCompare(b));
        if (!cancelled) setStructureOptions(["All", ...names]);
      } catch {
        if (!cancelled) setStructureOptions(["All"]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAssignedStructure]);

  const fetchEmployeeOptions = useCallback(async () => {
    const response = await employeeService.getEmployees({
      page: 1,
      limit: 400,
      excludeEmploymentTypes: "External,external",
    });
    let list = [];
    if (Array.isArray(response?.data)) list = response.data;
    else if (Array.isArray(response?.employees)) list = response.employees;
    return { data: list };
  }, []);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await payrollOpsService.listActionRecords(actionKey, {
        q: listQ.trim() || undefined,
        financialYear: financialYear || undefined,
        limit: 500,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setVisibleCount(PAGE_SIZE);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load records");
      setRows([]);
      setVisibleCount(PAGE_SIZE);
    } finally {
      setLoadingList(false);
    }
  }, [actionKey, listQ, financialYear]);

  useEffect(() => {
    if (panel !== "list") return undefined;
    const t = setTimeout(() => {
      loadList();
    }, listQ ? 280 : 0);
    return () => clearTimeout(t);
  }, [panel, loadList, listQ]);

  const statuses = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const s = displayLabel(r.status, "");
      if (s) set.add(s);
    });
    return ["all", ...[...set].sort()];
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return rows;
    return rows.filter((r) => displayLabel(r.status, "") === statusFilter);
  }, [rows, statusFilter]);

  const visibleRows = useMemo(
    () => filteredRows.slice(0, visibleCount),
    [filteredRows, visibleCount]
  );
  const hasMore = filteredRows.length > visibleCount;

  const openRow = async (row) => {
    const id = Number(row?.employeeId || row?.id);
    if (!id) {
      toast.error("Invalid employee");
      return;
    }
    let emp = {
      id,
      employeeId: id,
      empCode: row.empCode,
      name: row.name,
      designation: row.designation,
      department: row.department,
      employmentStatus: row.employmentStatus,
      ...row,
    };
    try {
      const res = await employeeService.getEmployee(id);
      const data = res?.data ?? res;
      if (data && (data.id || data.empCode)) {
        emp = { ...emp, ...data, id: data.id || id };
      }
    } catch {
      /* use list row */
    }
    onOpenEmployee?.(emp);
  };

  const handleProcess = () => {
    if (!employeeId) {
      toast.error("Select an employee first");
      return;
    }
    openRow(
      picked || {
        employeeId: Number(employeeId),
        id: Number(employeeId),
      }
    );
  };

  const handleExportStructureReport = async () => {
    const ym = String(reportMonth || "").slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(ym)) {
      toast.error("Select a valid pay month");
      return;
    }
    setExporting(true);
    try {
      const res = await payrollOpsService.getStructureAsOfReport({
        payMonth: ym,
        structureName: structureFilter || "All",
      });
      const data = res?.data ?? res;
      if (Array.isArray(data?.structureOptions) && data.structureOptions.length) {
        setStructureOptions(data.structureOptions);
      }
      const filename = await exportAssignedStructureReportExcel(data);
      toast.success(filename ? `Downloaded ${filename}` : "Structure report downloaded");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to export structure report"
      );
    } finally {
      setExporting(false);
    }
  };

  void darkMode;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg bg-[hsl(var(--ds-primary)/0.08)] p-1 pace-action-rail-icon">
            {Icon3d ? <Icon3d size={24} /> : null}
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold tracking-tight text-[hsl(var(--ds-fg))]">
              {title}
            </h2>
            {blurb ? <p className={`text-[11px] ${muted}`}>{blurb}</p> : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isAssignedStructure ? (
            <div className="flex flex-wrap items-center gap-2">
              <ActionInfoTip
                text={
                  "Export the Fixed salary structure package as of the selected pay month for all payroll-listable employees. " +
                  "Includes CTC, structure name, effective date, every component amount, Gross, deductions, Net, and employer benefits. " +
                  "Structure filter defaults to All; pick a named structure to export only that template. " +
                  "Amounts are structure Fixed values (not Process paidDays / live PF segments) — use this to audit package vs Payroll Register."
                }
              />
              <label className={`flex items-center gap-1.5 text-[11px] ${muted}`}>
                <span className="sr-only">Pay month</span>
                <input
                  type="month"
                  value={reportMonth}
                  onChange={(e) => setReportMonth(e.target.value)}
                  className={`h-9 rounded-lg border bg-[hsl(var(--ds-surface))] px-2 text-xs text-[hsl(var(--ds-fg))] ${border}`}
                  aria-label="Report pay month"
                />
              </label>
              <select
                value={structureFilter}
                onChange={(e) => setStructureFilter(e.target.value)}
                className={`h-9 max-w-[11rem] rounded-lg border bg-[hsl(var(--ds-surface))] px-2 text-xs text-[hsl(var(--ds-fg))] ${border}`}
                aria-label="Structure filter"
              >
                {structureOptions.map((s) => (
                  <option key={s} value={s}>
                    {s === "All" ? "All structures" : s}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={exporting || !reportMonth}
                onClick={handleExportStructureReport}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[hsl(var(--ds-primary))] px-3 text-xs font-semibold text-[hsl(var(--ds-primary-fg))] disabled:opacity-45"
              >
                {exporting ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Download size={13} />
                )}
                Export
              </button>
            </div>
          ) : null}

          <div className={`inline-flex rounded-xl border ${border} bg-white p-0.5 dark:bg-[hsl(var(--ds-surface))]`}>
            <button
              type="button"
              onClick={() => setPanel("process")}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                panel === "process"
                  ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                  : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
              }`}
            >
              <UserPlus size={14} /> Process
            </button>
            <button
              type="button"
              onClick={() => setPanel("list")}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                panel === "list"
                  ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                  : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
              }`}
            >
              <List size={14} /> {meta.listTitle}
            </button>
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {panel === "process" ? (
          <ActionProcessCard
            darkMode={darkMode}
            title={meta.processTitle}
            infoText={meta.processHint}
            selectId={`action-process-${actionKey}`}
            employeeId={employeeId}
            onEmployeeChange={(e) => {
              const v = String(e?.target?.value ?? "");
              setEmployeeId(v);
              setPicked(v ? { id: Number(v), employeeId: Number(v) } : null);
            }}
            fetchOptions={fetchEmployeeOptions}
            primaryLabel={meta.openLabel || openLabel || "Open"}
            onPrimary={handleProcess}
            showSecondary
            secondaryLabel="View list"
            onSecondary={() => setPanel("list")}
          />
        ) : (
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="relative min-w-[14rem] flex-1">
                <Search
                  size={13}
                  className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`}
                />
                <input
                  type="search"
                  value={listQ}
                  onChange={(e) => {
                    setListQ(e.target.value);
                    setVisibleCount(PAGE_SIZE);
                  }}
                  placeholder="Search code, name, dept…"
                  className={`h-9 w-full rounded-lg border bg-[hsl(var(--ds-surface))] pl-8 pr-3 text-xs ${border}`}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setVisibleCount(PAGE_SIZE);
                }}
                className={`h-9 rounded-lg border bg-[hsl(var(--ds-surface))] px-2 text-xs ${border}`}
                aria-label="Status filter"
              >
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s === "all" ? "All statuses" : s}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={loadList}
                className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))]`}
              >
                <RefreshCw size={13} /> Refresh
              </button>
              <button
                type="button"
                onClick={() => setPanel("process")}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[hsl(var(--ds-primary))] px-3 text-xs font-semibold text-[hsl(var(--ds-primary-fg))]"
              >
                <Plus size={13} /> New
              </button>
            </div>

            <div className={`relative min-h-0 flex-1 overflow-auto rounded-xl border bg-[hsl(var(--ds-surface))] ${border}`}>
              <table className="min-w-full text-left text-xs">
                <thead className="sticky top-0 z-[1] bg-[hsl(var(--ds-primary)/0.08)]">
                  <tr className="border-b border-[hsl(var(--ds-primary)/0.2)]">
                    <th className="px-3 py-2.5 font-semibold">Sl No</th>
                    <th className="px-3 py-2.5 font-semibold">Emp Code</th>
                    <th className="px-3 py-2.5 font-semibold">Name</th>
                    <th className="px-3 py-2.5 font-semibold">Designation</th>
                    <th className="px-3 py-2.5 font-semibold">Department</th>
                    <th className="px-3 py-2.5 font-semibold">Status</th>
                    {extraCols.map((c) => (
                      <th
                        key={c.key}
                        className={`px-3 py-2.5 font-semibold ${
                          c.align === "right" ? "text-right" : ""
                        }`}
                      >
                        {c.label}
                      </th>
                    ))}
                    <th className="px-3 py-2.5 text-right font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingList ? (
                    <tr>
                      <td
                        colSpan={7 + extraCols.length}
                        className={`px-3 py-12 text-center ${muted}`}
                      >
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" /> Loading records…
                        </span>
                      </td>
                    </tr>
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7 + extraCols.length}
                        className={`px-3 py-12 text-center ${muted}`}
                      >
                        <p className="font-medium text-[hsl(var(--ds-fg))]">{meta.emptyList}</p>
                        <button
                          type="button"
                          onClick={() => setPanel("process")}
                          className={`mt-3 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))]`}
                        >
                          <UserPlus size={13} /> Process new
                        </button>
                      </td>
                    </tr>
                  ) : (
                    visibleRows.map((row, idx) => (
                      <tr
                        key={`${row.id || row.employeeId}-${idx}`}
                        className="border-b border-[hsl(var(--ds-primary)/0.1)] last:border-0 hover:bg-[hsl(var(--ds-primary)/0.04)]"
                      >
                        <td className="px-3 py-2.5 tabular-nums">{idx + 1}</td>
                        <td className="px-3 py-2.5 font-semibold">
                          {displayLabel(row.empCode, "")}
                        </td>
                        <td className="px-3 py-2.5">{employeeDisplayName(row)}</td>
                        <td className="px-3 py-2.5">{displayLabel(row.designation)}</td>
                        <td className="px-3 py-2.5">{displayLabel(row.department)}</td>
                        <td className="px-3 py-2.5">
                          <span
                            className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone(
                              row.status
                            )}`}
                          >
                            {displayLabel(row.status, "—")}
                          </span>
                        </td>
                        {extraCols.map((c) => (
                          <td
                            key={c.key}
                            className={`px-3 py-2.5 ${
                              c.align === "right" ? "text-right tabular-nums" : ""
                            }`}
                          >
                            {typeof c.render === "function"
                              ? c.render(row)
                              : displayLabel(row[c.key])}
                          </td>
                        ))}
                        <td className="px-3 py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => openRow(row)}
                            className="inline-flex items-center gap-1 rounded-lg bg-[hsl(var(--ds-primary))] px-2.5 py-1.5 text-[11px] font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"
                          >
                            {meta.openLabel || openLabel || "Open"} <ChevronRight size={12} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {!loadingList && filteredRows.length > 0 ? (
              <div className="relative mt-2 flex shrink-0 items-center justify-between gap-2">
                <p className={`text-[11px] ${muted}`}>
                  Showing {visibleRows.length} of {filteredRows.length} record
                  {filteredRows.length === 1 ? "" : "s"}
                </p>
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--ds-primary)/0.4)] bg-white px-3.5 py-1.5 text-[11px] font-semibold text-[hsl(var(--ds-primary))] shadow-md hover:bg-[hsl(var(--ds-primary)/0.06)] dark:bg-[hsl(var(--ds-surface))]"
                  >
                    Load more
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
